import { useEffect, useState } from "react";
import { X, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { nextDay, onCheckinChange } from "../../lib/dates";

export function EditBookingModal({ booking, onClose, onSaved }) {
  const [form, setForm] = useState({
    checkin: booking.checkin, checkout: booking.checkout,
    adults: booking.adults ?? booking.guests ?? 2, children: booking.children ?? 0, status: booking.status,
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!form.checkin || !form.checkout) return;
      setLoading(true);
      try {
        const { data } = await api.post("/admin/bookings/quote", {
          apartment_id: booking.apartment_id, room_type_id: booking.room_type_id,
          checkin: form.checkin, checkout: form.checkout, adults: form.adults, children: form.children,
        });
        setQuote(data);
      } catch (e) { setQuote(null); }
      finally { setLoading(false); }
    };
    run();
  }, [form.checkin, form.checkout, form.adults, form.children, booking.apartment_id, booking.room_type_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/bookings/${booking.id}`, form);
      toast.success(form.status === "cancelled" ? "Prenotazione cancellata (anche su Smoobu se sincronizzata)" : "Prenotazione aggiornata");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Eliminare questa prenotazione? Se sincronizzata, verrà cancellata anche su Smoobu.")) return;
    try {
      const { data } = await api.delete(`/admin/bookings/${booking.id}`);
      toast.success(data.smoobu_cancelled ? "Prenotazione eliminata e cancellata su Smoobu" : "Prenotazione eliminata");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-fir-deep/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ivory rounded-3xl w-full max-w-lg p-7 my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-booking-form">
        <div className="flex justify-between items-center mb-5">
          <h2 className="font-display text-3xl text-fir">Modifica prenotazione</h2>
          <button onClick={onClose}><X className="text-fir" /></button>
        </div>
        <p className="text-sm text-fir-deep/60 mb-4">{booking.guest_name} · {booking.apartment_title}{booking.channel ? ` · ${booking.channel}` : ""}</p>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Check-in</label>
            <input type="date" data-testid="edit-checkin" value={form.checkin} onChange={(e) => onCheckinChange(e.target.value, form.checkout, setForm)} className="input-alpine w-full mt-1" /></div>
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Check-out</label>
            <input type="date" data-testid="edit-checkout" value={form.checkout} min={nextDay(form.checkin)} onChange={(e) => setForm({ ...form, checkout: e.target.value })} className="input-alpine w-full mt-1" /></div>
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Adulti</label>
            <input type="number" min="1" data-testid="edit-adults" value={form.adults} onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })} className="input-alpine w-full mt-1" /></div>
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Bambini (&lt;14)</label>
            <input type="number" min="0" data-testid="edit-children" value={form.children} onChange={(e) => setForm({ ...form, children: Number(e.target.value) })} className="input-alpine w-full mt-1" /></div>
          <div className="col-span-2"><label className="text-xs uppercase tracking-wide text-fir-deep/60">Stato</label>
            <select data-testid="edit-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-alpine w-full mt-1">
              <option value="confirmed">Confermata</option>
              <option value="pending">In attesa</option>
              <option value="cancelled">Cancellata</option>
            </select></div>
        </div>

        <div data-testid="edit-quote" className="rounded-2xl border border-wood/40 bg-white p-4 mb-4">
          {loading ? <Loader2 className="animate-spin text-fir mx-auto" /> : quote ? (
            <>
              <div className="flex justify-between text-sm text-fir-deep/70">
                <span>{quote.num_nights} notti{quote.padded_nights > 0 ? ` (fatturate ${quote.billed_nights})` : ""} · {form.adults} ad.{form.children ? `, ${form.children} bimbi` : ""}</span>
                <span>€{quote.avg_per_person_night}/persona/notte</span>
              </div>
              <div className="flex justify-between items-end mt-2 pt-2 border-t border-stone">
                <span className="font-display text-lg text-fir">Nuovo totale</span>
                <span data-testid="edit-total" className="font-display text-3xl text-fir">€{quote.total}</span>
              </div>
            </>
          ) : <p className="text-sm text-fir-deep/50">Imposta date valide per il ricalcolo.</p>}
        </div>

        <div className="flex gap-3">
          <button data-testid="save-booking-btn" onClick={save} disabled={saving} className="gold-btn flex-1 py-3 rounded-xl flex items-center justify-center gap-2">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Salva modifiche"}
          </button>
          <button data-testid="delete-booking-modal-btn" onClick={del}
            className="px-4 py-3 rounded-xl border border-red-300 text-red-600 hover:bg-red-600 hover:text-white transition-colors flex items-center gap-2">
            <Trash2 size={16} /> Elimina
          </button>
        </div>
      </div>
    </div>
  );
}
